/**
 * `SanitizedHtml` — the single sanctioned wrapper for rendering
 * arbitrary HTML strings into the React tree under `apps/web/`.
 *
 * Phase A posture (frontend-only sanitisation): the `html` input is
 * hand-authored fixture / seed HTML at the moment, and write-time
 * server sanitisation does not yet exist. Until Phase B introduces
 * admin authoring with backend sanitisation on write, this wrapper
 * is the only place a `dangerouslySetInnerHTML` may appear in
 * production source. The architecture check
 * `apps/web/src/tests/architecture/no-unsanitised-html.ts` enforces
 * that invariant — any other site that emits
 * `dangerouslySetInnerHTML` (a JSX attribute or a property of an
 * object literal passed to `React.createElement`) fails CI.
 *
 * **Spec references**
 *
 *   - `specs/011-pwa-prototype-convergence/spec.md` §Non-Inheritance
 *     Rule 13 ("No unsanitised HTML"); FR-049 ("Continuous
 *     integration MUST fail if `dangerouslySetInnerHTML` is used
 *     without a sanitisation wrapper").
 *   - `specs/003-content-and-knowledge/tasks.md` TA19 + TA20.x
 *     (the `ArticleDetailPage` and the bespoke routes consume this
 *     wrapper).
 *   - `specs/003-content-and-knowledge/tasks.md` TA25 (this file).
 *   - `docs/governance/decisions/D-043.md` D-043 (live-registry
 *     verification process for the `dompurify@3.4.2` pin).
 *
 * **Configuration choice — shared policy**. The config object is
 * sourced from `@omaplus/shared-sanitizer` so that the read-time
 * sanitisation policy here and the write-time sanitisation policy in
 * `apps/api` are a single artefact rather than parallel conventions
 * (021-cms FR-026, D-078 Proposed). The factory currently returns
 * DOMPurify's defaults — those already strip `<script>`, on-event
 * attributes, `javascript:` URIs, and `<iframe>` / `<object>` /
 * `<embed>`. Additionally, `installDataUriGuardHook` (registered at
 * module load below) strips `data:` URIs from `src` / `href` /
 * `xlink:href` on the media-tag carrier set (`img`, `audio`, `video`,
 * `source`, `image`, `track`) so SVG-payload stored-XSS via
 * `<img src="data:image/svg+xml;…">` cannot survive the read path.
 * Any future tightening (e.g. an explicit `ALLOWED_TAGS` allowlist)
 * lands in `packages/shared-sanitizer/src/index.ts` and is picked up
 * by both ends without a code change here.
 *
 * **Server-side rendering**. The web workspace is a pure SPA (see
 * `apps/web/src/main.tsx` + `apps/web/vite.config.ts` — no
 * `renderToString` / SSR build). DOMPurify therefore runs in the
 * browser environment with the native `window.DOMParser`; no
 * `jsdom`-adapter shape is needed. Tests that render this component
 * must run under jsdom: under happy-dom DOMPurify garbles its output,
 * and `apps/web/vitest.setup.ts` makes `sanitize()` throw there.
 *
 * **Type-cast at the boundary**. DOMPurify 3.4.x ships its own
 * types; the `sanitize(dirty: string, cfg?: Config): string`
 * overload is what this wrapper invokes. No `@types/dompurify` is
 * installed (the DefinitelyTyped package is a deprecated stub —
 * verified live on the npm registry on 2026-05-05). The `__html`
 * payload is a fresh `string`, so no `as unknown` hop is required;
 * `dangerouslySetInnerHTML` accepts `{ __html: string }` directly.
 */

import {
  getDompurifyConfig,
  installDataUriGuardHook,
  installStyleAllowlistHook,
  installVideoSrcSchemeGuardHook,
} from '@omaplus/shared-sanitizer';
import DOMPurify from 'dompurify';
import type { HTMLAttributes, ReactElement, Ref } from 'react';
import { createElement } from 'react';

// Module-load hook registration. The browser-side `dompurify` instance
// has a separate hook registry from the `isomorphic-dompurify` instance
// used by the server-side write-time sanitiser in apps/api — each
// consumer registers the guard against its own import. See
// `installDataUriGuardHook`'s docblock in @omaplus/shared-sanitizer
// for why per-consumer registration is required and the future-
// declarative migration path (DOMPurify 3.5.x `FORBID_DATA_URI_TAGS`).
// This call MUST stay at module top-level so the hook is installed
// before any render-time `DOMPurify.sanitize` invocation; ES module
// evaluation order guarantees the hook is registered before the
// component function below is exported.
installDataUriGuardHook(DOMPurify);
// `<video>`/`<source>` positive-attr allowlist + `src` scheme guard (024-cms #9):
// strips inline `style` and rejects a non-http(s)/non-same-origin `src`
// (javascript:/data:/vbscript:) so an author-controlled video embed cannot carry
// active content into the member read surface.
installVideoSrcSchemeGuardHook(DOMPurify);
// Inline-`style` allowlist (024-cms #9 hardening): DOMPurify does no CSS
// filtering, so keep ONLY the editor's `color: var(--color-*)` and strip every
// other style — closes CSS exfil (`background:url(evil)`) + UI-redress
// (`position:fixed`) on the member read surface.
installStyleAllowlistHook(DOMPurify);

/**
 * Wrapper-element tag. Kept narrow on purpose: every Phase A caller
 * mounts a `<div>` (`ArticleDetailPage` body, bespoke route body
 * panes); a `<section>` or `<article>` may surface as future
 * callers' semantics demand. Adding tags here is a deliberate
 * extension, not an opt-in escape hatch.
 */
export type SanitizedHtmlTag = 'div' | 'section' | 'article';

/**
 * Props for `SanitizedHtml`.
 *
 * - `html`: the untrusted HTML string. Sanitised by DOMPurify
 *   (defaults) before reaching the DOM. `null` / `undefined` are
 *   not accepted — the caller MUST coerce to `''` (or guard the
 *   render entirely) so the wrapper's contract reads as
 *   "non-nullable string in, sanitised string out".
 * - `as`: the wrapping element tag. Defaults to `'div'`.
 * - `className`: forwarded to the wrapping element. Used by callers
 *   to bind Tailwind utility classes (typography prose styles, body
 *   spacing tokens) on the surrounding container without leaking
 *   styles into the sanitised content.
 * - `ref`: forwarded ref to the wrapping element, for callers that
 *   need to measure / scroll the rendered region.
 *
 * Other native attributes (`aria-*`, `data-*`, `role`, etc.) are
 * forwarded via the `HTMLAttributes` extension so callers can pin a
 * landmark name (`aria-labelledby`), drop test handles (`data-testid`),
 * etc. without bespoke prop plumbing. `dangerouslySetInnerHTML` is
 * structurally excluded — it is the wrapper's reason for existing.
 */
export interface SanitizedHtmlProps
  extends Omit<HTMLAttributes<HTMLElement>, 'dangerouslySetInnerHTML'> {
  readonly html: string;
  readonly as?: SanitizedHtmlTag;
  readonly ref?: Ref<HTMLElement>;
}

/**
 * Sanitise an arbitrary HTML string with DOMPurify and render it
 * into the chosen wrapping element via `dangerouslySetInnerHTML`.
 *
 * The architecture check excludes this file (and only this file)
 * from the "no unsanitised HTML" invariant; every other site under
 * `apps/web/src/**` that touches `dangerouslySetInnerHTML` is a CI
 * violation. Tests for this wrapper colocate under
 * `__tests__/SanitizedHtml.test.tsx` and are excluded from the arch
 * check by the shared `__tests__/**` rule.
 */
export function SanitizedHtml({
  html,
  as = 'div',
  ref,
  ...rest
}: SanitizedHtmlProps): ReactElement {
  // DOMPurify's `sanitize(dirty, cfg?): string` overload returns a
  // plain string when no `RETURN_DOM*` / `RETURN_TRUSTED_TYPE` flag
  // is set in the config — `getDompurifyConfig()` returns no such
  // flag, so the return type is `string` directly. No cast needed.
  const sanitized = DOMPurify.sanitize(html, getDompurifyConfig());

  // `createElement` (rather than JSX) keeps the `as` tag forwarded
  // as a runtime value without paying the type-narrowing gymnastics
  // JSX would impose on a union of intrinsic tags. The prop bag
  // spreads any caller-supplied native attributes (`aria-*`,
  // `data-*`, `role`, `id`, `className`, etc.) and pins
  // `dangerouslySetInnerHTML` last so the sanitised HTML cannot be
  // overridden by an unsanitised forward.
  // Under `exactOptionalPropertyTypes`, spreading the destructured `...rest`
  // (whose optional members are `T | undefined`) into a value annotated
  // `HTMLAttributes<HTMLElement>` (whose optionals are bare `T`) is a type
  // error, as is assigning a possibly-`undefined` `ref` into an optional
  // `ref?: Ref<…>`. Both are spurious here — an explicitly-`undefined`
  // attribute is identical to an absent one for `createElement`. Keep the
  // intersection's optionals `| undefined` to match the real value shape,
  // and only attach `ref` when the caller actually supplied one.
  const props: HTMLAttributes<HTMLElement> & { ref?: Ref<HTMLElement> | undefined } = {
    ...rest,
    ...(ref ? { ref } : {}),
    dangerouslySetInnerHTML: { __html: sanitized },
  };

  return createElement(as, props);
}
